import { FC, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode.react';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { CurrencyEnum, CurrencyIcon } from '@spectrocoin/sc-currencies';
import { getOrderCancel, getOrderData } from '../../../../redux/OrderSlice/OrderSelectors';
import CopyInfoField from '../../../../components/CopyInfoField/CopyInfoField';
import ProgressBar from '../../../../components/ProgressBar/ProgressBar';
import Countdown from '../../../../components/Countdown/Countdown';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import CopyButton from '../../../../components/CopyButton/CopyButton';

import styles from './OrderBody.module.scss';
import { useAppDispatch } from '../../../../redux/Store';
import OrderActions from '../../../../redux/OrderSlice/OrderActions';
import { MerchantOrderStatus } from '../../../../interfaces/MerchantOrder';
import { formatPrecision, getQRValue } from '../../../../helpers/currencyHelper/currencyHelper';
import { getErrorMessageOrDefault } from '../../../../helpers/errorMessageHelper/errorMessageHelper';
import TestIds from '../../../../tests/TestIds';

export const messages = defineMessages({
	scanThisQrCode: {
		id: 'payments.scan_this_qr_code',
		defaultMessage: 'Scan this QR code with your wallet or copy details below',
	},
	coin: {
		id: 'payments.coin',
		defaultMessage: 'Coin',
	},
	network: {
		id: 'payments.network',
		defaultMessage: 'Network',
	},
	amount: {
		id: 'payments.amount',
		defaultMessage: 'Amount',
	},
	address: {
		id: 'payments.address',
		defaultMessage: 'Address',
	},
	message: {
		id: 'payments.message',
		defaultMessage: 'Message',
	},
	destinationTag: {
		id: 'payments.destination_tag',
		defaultMessage: 'Destination tag',
	},
	memo: {
		id: 'payments.memo',
		defaultMessage: 'Memo',
	},
	orderValidFor: {
		id: 'payments.order_valid_for_x',
		defaultMessage: 'Order valid for {Timer}',
	},
	awaitingPayment: {
		id: 'payments.awaiting_payment',
		defaultMessage: 'Awaiting payment',
	},
	depositMessageWarning: {
		id: 'payments.deposit_message_warning',
		defaultMessage:
			'The message above must be included in the payment; otherwise, the funds will be lost',
	},
	depositDestinationTagWarning: {
		id: 'payments.deposit_destination_tag_warning',
		defaultMessage: 'The Destination tag is required',
	},
	depositMemoWarning: {
		id: 'payments.deposit_memo_warning',
		defaultMessage:
			'The memo (if receiving wallet requires it) must be included in the payment, or your funds will be lost',
	},
	or: {
		id: 'payments.or',
		defaultMessage: 'Or',
	},
	payWithSc: {
		id: 'payments.pay_with_sc',
		defaultMessage: 'Pay with SpectroCoin',
	},
	instant: {
		id: 'payments.instant',
		defaultMessage: 'Instant',
	},
	infoDisclaimer: {
		id: 'payments.info_disclaimer',
		defaultMessage:
			'Payments processed by SpectroCoin Commerce. Be sure you trust the merchant listed, as transactions are final and refunds are handled by the merchant.',
	},
	usdtInfoMessage: {
		id: 'payments.usdt_info_message',
		defaultMessage:
			'Only transfer USDT on the Tron (TRC-20) network. Payments of other assets or on other networks will be lost',
	},
	cancel: {
		id: 'payments.cancel',
		defaultMessage: 'Cancel',
	},
});

const calculateProgress = (starDate: string, endDate: string) => {
	if (!starDate && !endDate) return 0;

	const validUntil = new Date(endDate).getTime();
	const duration = Math.floor(validUntil - new Date(starDate).getTime());
	const timeLeft = Math.floor(validUntil - new Date().getTime());
	return parseFloat(Number(100 - (timeLeft / duration) * 100).toFixed(2));
};

export const OrderCountdown: FC<{ endDate: string }> = ({ endDate }) => {
	const { formatMessage } = useIntl();

	return (
		<div className={styles.countdown} data-cy={TestIds.OrderCountdown}>
			{formatMessage(messages.awaitingPayment)}
			<Countdown endDate={endDate} />
		</div>
	);
};

const OrderBody: FC<{ isProgressBarDisabled?: boolean; remainingAmount?: string }> = ({
	isProgressBarDisabled = false,
	remainingAmount,
}) => {
	const { locale, formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const {
		id,
		depositAddress,
		payAmount,
		payCurrencyCode,
		payCurrencyName,
		payNetworkName,
		depositMessage,
		created,
		validUntil,
	} = useSelector(getOrderData)!;
	const { error } = useSelector(getOrderCancel);

	const [progress, setProgress] = useState(calculateProgress(created, validUntil));

	const additionalInformationLabel = useMemo(() => {
		if (!depositMessage) return null;
		switch (payCurrencyCode) {
			case CurrencyEnum.XEM:
				return messages.message;
			case CurrencyEnum.XRP:
				return messages.destinationTag;
			case CurrencyEnum.XLM:
				return messages.memo;
			default:
				return null;
		}
	}, [depositMessage, payCurrencyCode]);

	const additionalInformationWarning = useMemo(() => {
		if (!depositMessage) return null;
		switch (payCurrencyCode) {
			case CurrencyEnum.XEM:
				return messages.depositMessageWarning;
			case CurrencyEnum.XRP:
				return messages.depositDestinationTagWarning;
			case CurrencyEnum.XLM:
				return messages.depositMemoWarning;
			default:
				return null;
		}
	}, [depositMessage, payCurrencyCode]);

	useEffect(() => {
		const interval = setInterval(
			() => setProgress(calculateProgress(created, validUntil)),
			100
		);
		return () => {
			clearInterval(interval);
		};
	}, [created, validUntil]);

	// if order expires manually set it to expired since BE scheduler might take time to finish processing
	useEffect(() => {
		if (new Date(validUntil).getTime() <= new Date().getTime())
			dispatch(OrderActions.setOrderStatus(MerchantOrderStatus.EXPIRED));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [progress]);

	return (
		<div className={styles.container} data-cy={TestIds.Order}>
			{!isProgressBarDisabled && <OrderCountdown endDate={validUntil} />}
			{!isProgressBarDisabled && <ProgressBar reversed progressPercentage={progress} />}
			<div className={styles.header}>{formatMessage(messages.scanThisQrCode)}</div>
			<div className={styles.qrContainer}>
				<div className={styles.qrWrapper}>
					<QRCode
						data-cy={TestIds.QR}
						value={getQRValue(
							payCurrencyCode,
							depositAddress!,
							depositMessage,
							remainingAmount || payAmount
						)}
					/>
				</div>
				<div className={styles.qrInfo}>
					<div className={styles.qrInfoRow}>
						<span className={styles.qrInfoLabel}>{formatMessage(messages.coin)}</span>
						<span className={styles.qrInfoCurrencyValue} data-cy={TestIds.OrderCoin}>
							<CurrencyIcon className={styles.icon} currencyType={payCurrencyCode} />
							{payCurrencyName}
						</span>
					</div>
					<div className={styles.qrInfoRow}>
						<span className={styles.qrInfoLabel}>
							{formatMessage(messages.network)}
						</span>
						<span data-cy={TestIds.OrderNetwork}>{payNetworkName}</span>
					</div>
					<div className={styles.qrInfoRow}>
						<span className={styles.qrInfoLabel}>{formatMessage(messages.amount)}</span>
						<span className={styles.qrInfoAmountValue} data-cy={TestIds.OrderAmount}>
							{`${formatPrecision(
								remainingAmount || payAmount,
								payCurrencyCode
							)} ${payCurrencyCode}`}
							<CopyButton
								className={styles.icon}
								dataToCopy={remainingAmount || payAmount}
							/>
						</span>
					</div>
				</div>
			</div>
			{error && (
				<MessageBlock
					withIcon
					data-cy={TestIds.OrderCancelError}
					type={MessageType.Error}
					style={BlockStyle.Border}
					message={formatMessage(getErrorMessageOrDefault(error))}
				/>
			)}
			<div className={styles.inputs}>
				<CopyInfoField
					copyContent={depositAddress || ''}
					label={formatMessage(messages.address)}
					infoMessage={
						payCurrencyCode === CurrencyEnum.USDT
							? formatMessage(messages.depositMessageWarning)
							: ''
					}
				>
					<span data-cy={TestIds.OrderAddress}>{depositAddress}</span>
				</CopyInfoField>
				{additionalInformationLabel && (
					<CopyInfoField
						copyContent={depositMessage || ''}
						label={formatMessage(additionalInformationLabel)}
					>
						<span data-cy={TestIds.OrderMessage}>{depositMessage}</span>
					</CopyInfoField>
				)}
				{additionalInformationWarning && (
					<MessageBlock
						data-cy={TestIds.OrderMessageWarning}
						type={MessageType.Info}
						style={BlockStyle.Fill}
						message={formatMessage(additionalInformationWarning)}
						withIcon
					/>
				)}
			</div>
			<span className={styles.orText}>{formatMessage(messages.or)}</span>
			<div className={styles.payOption}>
				<div className={styles.name} data-cy={TestIds.OrderPayWithSc}>
					<FontAwesomeIcon className={styles.icon} icon={faHeart} />
					<span>{formatMessage(messages.payWithSc)}</span>
				</div>
				<div className={styles.action}>
					<span className={styles.badge}>{formatMessage(messages.instant)}</span>
					<a
						href={`/${locale}/payments/orders/${id}`}
						target="_blank"
						rel="noreferrer"
						aria-label={formatMessage(messages.payWithSc)}
					>
						<FontAwesomeIcon
							className={styles.linkIcon}
							icon={faArrowUpRightFromSquare}
						/>
					</a>
				</div>
			</div>
			<div className={styles.infoDisclaimer}>{formatMessage(messages.infoDisclaimer)}</div>
		</div>
	);
};

export default OrderBody;
